import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { formatDate, formatTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Star, Sparkles } from "lucide-react";

function OverrideVenue({ outingId, venues, onDone }) {
    const [venueId, setVenueId] = useState("");
    const set = async () => {
        if (!venueId) return;
        try {
            await api.post(`/admin/outings/${outingId}/set-venue`, { venue_id: venueId });
            toast.success("Internal venue set — members notified.");
            onDone();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return (
        <div className="mt-4 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3" data-testid={`venue-override-${outingId}`}>
            <p className="text-sm font-600 text-zinc-700">None of these fit?</p>
            <p className="text-xs text-zinc-500">Reject the AI picks and choose a venue from your internal list instead.</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <select data-testid={`override-select-${outingId}`} value={venueId} onChange={(e) => setVenueId(e.target.value)}
                    className="min-w-[220px] rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none">
                    <option value="">Select an internal venue…</option>
                    {(venues || []).map((v) => (
                        <option key={v.id} value={v.id}>{v.name}{v.city ? ` — ${v.city}` : ""}</option>
                    ))}
                </select>
                <Button size="sm" variant="outline" data-testid={`override-set-${outingId}`} onClick={set} disabled={!venueId}>Set this venue</Button>
            </div>
        </div>
    );
}

export default function VenueApprovals() {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["venue-approvals"], queryFn: async () => (await api.get("/admin/venue-approvals")).data });
    const { data: venues } = useQuery({ queryKey: ["admin-venues"], queryFn: async () => (await api.get("/admin/venues")).data });

    const refresh = () => qc.invalidateQueries({ queryKey: ["venue-approvals"] });

    const approve = async (outingId, index) => {
        try {
            await api.post(`/admin/outings/${outingId}/approve-venue`, { index });
            toast.success("Venue approved — members notified.");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const rows = data || [];
    return (
        <div>
            <AdminHeader title="Venue Approvals" subtitle="AI-suggested venues from Google Maps for each confirmed outing. Approve one — or override with a venue from your list." />
            {rows.length === 0 && <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid="venue-approvals-empty">No venues awaiting approval right now.</div>}
            <div className="space-y-5">
                {rows.map((o) => (
                    <div key={o.id} data-testid={`venue-approval-${o.id}`} className="rounded-lg border border-zinc-200 bg-white p-5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <p className="text-lg font-600">{o.activity_emoji} {o.activity_name}</p>
                                <p className="text-sm text-zinc-500">{formatDate(o.date)} · {formatTime(o.start_time)} – {formatTime(o.end_time)} · {o.market} · {o.participant_count} members</p>
                            </div>
                            <Badge tone="amber">Pending approval</Badge>
                        </div>
                        {o.candidates.length === 0 ? (
                            <p className="mt-3 text-sm text-zinc-500">No AI suggestions available (Google Places may be off). Choose a venue below.</p>
                        ) : (
                            <div className="mt-4 space-y-2">
                                {o.candidates.map((c, i) => {
                                    const isPick = i === o.ai_pick_index;
                                    return (
                                        <div key={i} data-testid={`venue-candidate-${o.id}-${i}`}
                                            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${isPick ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}>
                                            <div className="flex min-w-0 items-center gap-3">
                                                {c.photo_name && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/venue-photo?name=${encodeURIComponent(c.photo_name)}`} alt="" className="h-14 w-14 shrink-0 rounded-md object-cover" />}
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-600">{c.name}</p>
                                                        {isPick && <span className="inline-flex items-center gap-1 rounded bg-zinc-900 px-1.5 py-0.5 text-[10px] font-600 text-white"><Sparkles className="h-3 w-3" /> AI pick</span>}
                                                    </div>
                                                    <p className="text-xs text-zinc-500">{c.address}</p>
                                                    <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                                                        {c.rating != null && <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {c.rating}</span>}
                                                        <span>{c.price_level}</span>
                                                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((c.name || "") + " " + (c.address || ""))}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open in Maps</a>
                                                        {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Website</a>}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button size="sm" data-testid={`approve-venue-${o.id}-${i}`} onClick={() => approve(o.id, i)}
                                                className={isPick ? "bg-zinc-900 text-white hover:bg-zinc-800" : ""} variant={isPick ? "default" : "outline"}>
                                                Approve
                                            </Button>
                                        </div>
                                    );
                                })}
                                {o.ai_reason && <p className="text-xs text-zinc-400">AI note: {o.ai_reason}</p>}
                            </div>
                        )}
                        <OverrideVenue outingId={o.id} venues={venues} onDone={refresh} />
                    </div>
                ))}
            </div>
        </div>
    );
}
